import { useState } from "react";
import { motion } from "framer-motion";
import { Calculator } from "lucide-react";

const RATE = 199; // per community / quarter at 30+ communities
const CORE_RATE = 299; // per property / quarter (standard plans)
const THRESHOLD = 30;
const MIN = 1;
const MAX = 250;

const fmt = (n) => `$${n.toLocaleString("en-US")}`;

export const VolumeCalculator = () => {
  const [communities, setCommunities] = useState(50);
  const qualifies = communities >= THRESHOLD;
  const quarterly = communities * RATE;
  const annual = quarterly * 4;
  const pct = ((communities - MIN) / (MAX - MIN)) * 100;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      data-testid="volume-calculator"
      className="mt-8 rounded-md border border-slate-200 bg-white p-6 sm:p-8"
    >
      <div className="flex flex-col lg:flex-row lg:items-center gap-8">
        <div className="flex-1">
          <p className="eyebrow mb-2 flex items-center gap-3">
            <span className="inline-block h-px w-10 bg-cadmium" /> Volume Estimator
          </p>
          <h3 className="font-cabinet text-xl sm:text-2xl font-bold tracking-tight text-slate-900">
            How many communities do you run?
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Operators with <span className="font-semibold text-slate-900">30+ communities</span> get the{" "}
            <span className="font-semibold text-slate-900">{fmt(RATE)} / community per quarter</span> volume rate.
          </p>

          <div className="mt-6">
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Communities</span>
              <span data-testid="vc-communities-value" className="font-mono text-2xl font-semibold text-slate-900">
                {communities}{communities === MAX ? "+" : ""}
              </span>
            </div>
            <input
              type="range"
              min={MIN}
              max={MAX}
              step={1}
              value={communities}
              onChange={(e) => setCommunities(Number(e.target.value))}
              data-testid="vc-slider"
              aria-label="Number of communities"
              className="w-full h-2 appearance-none rounded-full cursor-pointer accent-cadmium"
              style={{ background: `linear-gradient(to right, #FF552E ${pct}%, #E2E8F0 ${pct}%)` }}
            />
            <div className="flex justify-between mt-1.5 text-[10px] font-mono uppercase tracking-wider text-muted-foreground/60">
              <span>{MIN}</span>
              <span>{MAX}+</span>
            </div>
            {!qualifies && (
              <p data-testid="vc-below-threshold-note" className="mt-4 text-sm text-slate-900/80 bg-amber-50 border border-amber-200 rounded-md px-4 py-3">
                Under {THRESHOLD} communities, our per-property plans apply — <span className="font-semibold text-slate-900">Core from {fmt(CORE_RATE)} / property per quarter</span>. Add{" "}
                <span className="font-semibold text-slate-900">{THRESHOLD - communities}</span> more {THRESHOLD - communities === 1 ? "community" : "communities"} to unlock the {fmt(RATE)} / community volume rate.
              </p>
            )}
          </div>
        </div>

        {qualifies ? (
          <div className="lg:w-72 shrink-0 rounded-md bg-[#E2EDF8] border border-slate-200 p-6 text-center">
            <p className="text-[10px] font-mono uppercase tracking-widest text-sky-600">Estimated quarterly total</p>
            <p data-testid="vc-quarterly-total" className="mt-1.5 font-mono text-4xl font-semibold text-slate-900 tracking-tight">
              {fmt(quarterly)}
            </p>
            <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mt-1">/ quarter</p>
            <p data-testid="vc-annual-total" className="mt-3 text-sm text-slate-900/80">
              ≈ {fmt(annual)} / year
            </p>
            <a
              href="#demo"
              data-testid="vc-cta"
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md bg-cadmium px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-cadmium-hover hover:-translate-y-0.5 active:scale-[0.98]"
            >
              <Calculator size={14} /> Lock in Volume Pricing
            </a>
          </div>
        ) : (
          <div data-testid="vc-per-property-card" className="lg:w-72 shrink-0 rounded-md bg-slate-50 border border-slate-200 p-6 text-center">
            <p className="text-[10px] font-mono uppercase tracking-widest text-sky-600">Per-property pricing</p>
            <p className="mt-1.5 font-mono text-4xl font-semibold text-slate-900 tracking-tight">{fmt(CORE_RATE)}</p>
            <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mt-1">/ property / quarter</p>
            <p className="mt-3 text-sm text-slate-900/80">Pro tier adds custom SOPs &amp; priority onboarding.</p>
            <a
              href="#pricing"
              data-testid="vc-cta"
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-900 transition-all duration-200 hover:border-cadmium hover:text-cadmium hover:-translate-y-0.5 active:scale-[0.98]"
            >
              <Calculator size={14} /> See Core &amp; Pro Plans
            </a>
          </div>
        )}
      </div>
    </motion.div>
  );
};
